import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Navigate } from 'react-router-dom';
import {
  FiSend,
  FiPlus,
  FiTrash2,
  FiDownload,
  FiUpload,
  FiRefreshCw,
  FiCopy,
  FiCpu,
  FiExternalLink,
  FiZap
} from 'react-icons/fi';
import toast from 'react-hot-toast';
import { fuelInjectionApi, authApi, tokenizersApi } from '../../api/smartTokenizerApi';
import { useTokenIssuer } from '../../contexts/TokenIssuerContext';
import { useClientConfig } from '../../contexts/ClientConfigContext';
import {
  copyTextToClipboard,
  downloadJsonFile,
  normalizePreparedTransactionResponse,
  isSuccessfulReceiptStatus,
  isFailedReceiptStatus
} from '../../blockchain/offlineSigning';

const Page = styled.div`
  max-width: min(920px, 100%);
  margin: 0 auto;
  padding: 2rem;
  min-height: calc(100vh - 80px);
`;

const Title = styled.h1`
  font-size: 1.75rem;
  margin-bottom: 0.5rem;
  color: #fff;
  display: flex;
  align-items: center;
  gap: 0.75rem;

  svg {
    color: #4facfe;
  }
`;

const Lead = styled.p`
  color: #94a3b8;
  margin-bottom: 1.5rem;
  line-height: 1.55;
  font-size: 0.95rem;
`;

const FormPanel = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  margin-bottom: 1rem;
  color: #94a3b8;
  font-size: 0.85rem;

  span.label {
    color: #e2e8f0;
  }

  input,
  select {
    padding: 0.55rem 0.65rem;
    border-radius: 8px;
    border: 1px solid rgba(255, 255, 255, 0.15);
    background: rgba(15, 15, 35, 0.8);
    color: #fff;
    font-size: 0.92rem;
    width: 100%;
    max-width: 100%;
  }
`;

const SectionDivider = styled.div`
  margin: 1.25rem 0;
  height: 1px;
  background: rgba(255, 255, 255, 0.08);
`;

const FlowSteps = styled.ol`
  margin: 0 0 1rem;
  padding-left: 1.25rem;
  color: #a0a0a0;
  line-height: 1.55;
  font-size: 0.9rem;

  li {
    margin-bottom: 0.5rem;
  }

  strong {
    color: #e2e8f0;
    font-weight: 600;
  }

  code {
    font-size: 0.85em;
    color: #9bd6ff;
  }
`;

const StatusPanel = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  color: #fff;

  .title {
    font-weight: 600;
    margin-bottom: 0.5rem;
    color: #4facfe;
  }

  .meta {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
    color: #a0a0a0;
    font-size: 0.9rem;
  }
`;

const PayloadBox = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;

  .title {
    color: #fff;
    font-weight: 600;
    margin-bottom: 0.5rem;
  }

  .hint {
    color: #a0a0a0;
    font-size: 0.9rem;
    margin-bottom: 1rem;
    line-height: 1.5;
  }

  textarea {
    width: 100%;
    min-height: 160px;
    background: rgba(15, 15, 35, 0.8);
    color: #fff;
    border: 1px solid rgba(255, 255, 255, 0.15);
    border-radius: 8px;
    padding: 1rem;
    resize: vertical;
    font-family: Consolas, Monaco, monospace;
    font-size: 0.85rem;
  }
`;

const PayloadActions = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
`;

const MiniButton = styled.button.attrs({ type: 'button' })`
  padding: 0.6rem 0.9rem;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: rgba(255, 255, 255, 0.06);
  color: #fff;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.12);
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const FilePickLabel = styled.label`
  padding: 0.6rem 0.9rem;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: rgba(255, 255, 255, 0.06);
  color: #fff;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  cursor: pointer;
  font-size: 0.9rem;

  &:hover {
    background: rgba(255, 255, 255, 0.12);
  }

  input {
    display: none;
  }
`;

const ActionRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 0.5rem;
  margin-bottom: 1.5rem;
  align-items: center;
`;

const PrimaryButton = styled.button.attrs({ type: 'button' })`
  padding: 0.75rem 1.25rem;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  background: #4facfe;
  color: #fff;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: #3d8bfe;
  }

  &:disabled {
    background: #2a2a3e;
    color: #a0a0a0;
    cursor: not-allowed;
  }
`;

const SecondaryButton = styled.button.attrs({ type: 'button' })`
  padding: 0.75rem 1.25rem;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  font-weight: 600;
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.14);
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const RowGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 140px auto;
  gap: 0.75rem;
  align-items: end;
  margin-bottom: 0.75rem;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const IconBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 8px;
  background: rgba(255, 80, 80, 0.15);
  border: 1px solid rgba(255, 100, 100, 0.35);
  color: #ffb4b4;

  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }
`;

const ManualSectionTitle = styled.h2`
  margin: 0 0 0.75rem;
  color: #e2e8f0;
  font-size: 1rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.35rem;
`;

const ResultsTableWrap = styled.div`
  overflow-x: auto;
  margin-top: 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
`;

const ResultsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.82rem;

  th,
  td {
    padding: 0.5rem 0.65rem;
    text-align: left;
    border-bottom: 1px solid rgba(255, 255, 255, 0.06);
    vertical-align: middle;
  }

  th {
    color: #94a3b8;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    font-size: 0.68rem;
  }

  td {
    color: #e2e8f0;
  }

  td.mono {
    font-family: ui-monospace, monospace;
    font-size: 0.78rem;
    word-break: break-all;
  }
`;

let rowKeySeq = 0;
const nextRowKey = () => ++rowKeySeq;

function registryEntityState(record) {
  const s = record?.status ?? record?.Status;
  if (s === null || s === undefined) return 'unknown';
  const lower = String(s).toLowerCase();
  if (lower === 'approved' || s === 2) return 'approved';
  if (lower === 'suspended' || s === 3) return 'suspended';
  if (lower === 'pending' || s === 1) return 'pending';
  return 'unknown';
}

function tokenizerOptionLabel(t) {
  const name = t.organizationName ?? t.OrganizationName ?? 'Tokenizer';
  const reg = t.registrationNumber ?? t.RegistrationNumber ?? '';
  const base = reg ? `${name} (${reg})` : name;
  const st = registryEntityState(t);
  if (st === 'approved') return base;
  if (st === 'pending') return `${base} — pending approval`;
  if (st === 'suspended') return `${base} — suspended`;
  return `${base} — not approved`;
}

function shortenAddr(addr) {
  const a = String(addr || '');
  if (a.length < 14) return a;
  return `${a.slice(0, 8)}…${a.slice(-6)}`;
}

const BATCH_SIGNER_TEMPLATE = `{
  "batchSignedTransactions": true,
  "summary": "…",
  "results": [
    {
      "signerAddress": "0x… or ICAN…",
      "signedTransactionHex": "0x…",
      "transactionHash": null,
      "expectedContractAddress": null,
      "summary": "…",
      "keySource": "…",
      "signedAtUtc": "…"
    }
  ]
}`;

function fuelInjectionStatusLabel(raw) {
  const s = raw === 2 || raw === '2' || String(raw).toLowerCase() === 'submitted' ? 'submitted' : 'prepared';
  return s === 'submitted' ? 'Submitted' : 'Prepared';
}

function FuelInjection() {
  const { isAdmin } = useTokenIssuer();
  const { explorerConfigured, transactionExplorerUrl } = useClientConfig();
  const [tokenizers, setTokenizers] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [signerAddress, setSignerAddress] = useState('');
  const [rows, setRows] = useState([{ key: nextRowKey(), authorizedTokenizerId: '', amountEth: '' }]);
  const [preparing, setPreparing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [batchId, setBatchId] = useState(null);
  const [preparedBatch, setPreparedBatch] = useState(null);
  const [signedPaste, setSignedPaste] = useState('');
  const [submitResults, setSubmitResults] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const preparedBatchJson = useMemo(
    () => (preparedBatch ? JSON.stringify(preparedBatch, null, 2) : ''),
    [preparedBatch]
  );

  const loadTokenizers = useCallback(async () => {
    setLoadingList(true);
    try {
      const { data } = await tokenizersApi.list();
      const list = Array.isArray(data) ? data : [];
      setTokenizers(list);
    } catch (e) {
      toast.error(e.response?.data?.message || e.message || 'Failed to load tokenizers');
    } finally {
      setLoadingList(false);
    }
  }, []);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const { data } = await fuelInjectionApi.history();
      setHistory(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(e.response?.data?.message || e.message || 'Failed to load fuel injection history');
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAdmin) loadTokenizers();
  }, [isAdmin, loadTokenizers]);

  useEffect(() => {
    if (isAdmin) loadHistory();
  }, [isAdmin, loadHistory]);

  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await authApi.getProfile();
        if (cancelled) return;
        const w = String(data?.walletAddress ?? data?.WalletAddress ?? '').trim();
        if (w) setSignerAddress((prev) => (String(prev || '').trim() ? prev : w));
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAdmin]);

  const approvedTokenizers = useMemo(
    () => tokenizers.filter((t) => registryEntityState(t) === 'approved'),
    [tokenizers]
  );

  const addRow = () => {
    setRows((r) => [...r, { key: nextRowKey(), authorizedTokenizerId: '', amountEth: '' }]);
  };

  const removeRow = (key) => {
    setRows((r) => (r.length <= 1 ? r : r.filter((x) => x.key !== key)));
  };

  const updateRow = (key, patch) => {
    setRows((r) => r.map((x) => (x.key === key ? { ...x, ...patch } : x)));
  };

  const buildPreparePayload = () => {
    const transfers = rows
      .map((row) => ({
        authorizedTokenizerId: String(row.authorizedTokenizerId || '').trim(),
        amountEth: String(row.amountEth || '').trim()
      }))
      .filter((x) => x.authorizedTokenizerId && x.amountEth);

    return {
      signerAddress: String(signerAddress || '').trim(),
      transfers
    };
  };

  const resetOfflineFlow = () => {
    setBatchId(null);
    setPreparedBatch(null);
    setSignedPaste('');
    setSubmitResults(null);
  };

  const handlePrepare = async () => {
    const payload = buildPreparePayload();
    if (!payload.signerAddress) {
      toast.error('Enter the admin wallet address (from) — Core ICAN or 0x.');
      return;
    }
    if (payload.transfers.length === 0) {
      toast.error('Add at least one tokenizer and amount.');
      return;
    }

    setPreparing(true);
    setSubmitResults(null);
    try {
      const { data } = await fuelInjectionApi.prepare(payload);
      const bid = data?.batchId ?? data?.BatchId ?? null;
      if (!bid) {
        toast.error('API did not return a batch id.');
        return;
      }
      setBatchId(bid);
      const transfers = data?.transfers ?? data?.Transfers ?? [];
      const txs = transfers.map((line) => {
        const rawPrep = line?.prepared ?? line?.Prepared;
        const prep = normalizePreparedTransactionResponse(rawPrep);
        const tid = line?.authorizedTokenizerId ?? line?.AuthorizedTokenizerId;
        const org = line?.organizationName ?? line?.OrganizationName ?? '';
        const toAddr = line?.toAddress ?? line?.ToAddress ?? '';
        const amt = line?.amountEth ?? line?.AmountEth ?? '';
        return {
          ...prep,
          meta: {
            authorizedTokenizerId: tid,
            organizationName: org,
            toAddress: toAddr,
            amountEth: amt
          }
        };
      });

      const fileBody = {
        batchPreparedTransactions: true,
        summary: `Fuel injection: ${txs.length} native XCB transfer(s) to authorized tokenizer wallets. Sign every entry in order with the offline signer.`,
        transactions: txs
      };

      setPreparedBatch(fileBody);
      toast.success(`Prepared ${txs.length} transaction(s). Copy or download the JSON below for offline signing.`);
    } catch (e) {
      const msg = e.response?.data?.message || e.response?.data?.Message || e.message || 'Prepare failed';
      toast.error(msg);
    } finally {
      setPreparing(false);
    }
  };

  const handleCopyBatchJson = async () => {
    if (!preparedBatchJson) return;
    await copyTextToClipboard(preparedBatchJson);
    toast.success('Copied batch JSON');
  };

  const handleDownloadBatchJson = () => {
    if (!preparedBatch) return;
    downloadJsonFile(`fuel-injection-prepared-${Date.now()}.json`, preparedBatch);
  };

  /**
   * Accepts offline signer output in several shapes:
   * - { results: [{ signedTransactionHex }, …] }
   * - { transactions: [{ …, signedTransactionHex }, …] } (merged prepared+signed)
   * - { signedTransactionHex } (single tx)
   * - ["0x…", …]
   */
  const parseSignedHexes = (raw) => {
    let t = String(raw || '').replace(/^\uFEFF/, '').trim();
    if (!t) throw new Error('Paste the signed JSON or choose a signed batch file.');
    // Paste from chat / markdown
    const fence = /^```(?:json)?\s*([\s\S]*?)```$/im.exec(t);
    if (fence) {
      t = fence[1].trim();
    }
    let parsed;
    try {
      parsed = JSON.parse(t);
    } catch (e) {
      throw new Error(
        'Invalid JSON. Paste only the signed output file contents (one JSON object or array), not the unsigned prepared batch unless each transaction includes signedTransactionHex.'
      );
    }

    if (Array.isArray(parsed)) {
      return parsed.map((h, i) => {
        if (typeof h !== 'string' || !h.trim()) {
          throw new Error(`Signed hex at index ${i} must be a non-empty string`);
        }
        return h.trim();
      });
    }

    if (parsed && typeof parsed === 'object') {
      const results = parsed.results ?? parsed.Results;
      if (Array.isArray(results) && results.length > 0) {
        return results.map((r, i) => {
          const hex = r.signedTransactionHex ?? r.SignedTransactionHex;
          if (!hex || typeof hex !== 'string') {
            throw new Error(`results[${i}] missing signedTransactionHex`);
          }
          return hex.trim();
        });
      }

      const txs = parsed.transactions ?? parsed.Transactions;
      if (Array.isArray(txs) && txs.length > 0) {
        const hexes = txs.map((tx, i) => {
          const hex = tx?.signedTransactionHex ?? tx?.SignedTransactionHex;
          if (!hex || typeof hex !== 'string' || !hex.trim()) {
            return null;
          }
          return hex.trim();
        });
        if (hexes.every(Boolean)) {
          return hexes;
        }
        const missing = hexes.findIndex((h) => !h);
        if (missing >= 0) {
          throw new Error(
            `transactions[${missing}] has no signedTransactionHex. Upload the signer output (with signed hex), not the unsigned "Prepare" JSON.`
          );
        }
      }

      const singleHex = parsed.signedTransactionHex ?? parsed.SignedTransactionHex;
      if (typeof singleHex === 'string' && singleHex.trim()) {
        return [singleHex.trim()];
      }
    }

    throw new Error(
      'Could not find signedTransactionHex. Paste the file your offline signer produced (object with signedTransactionHex, or results[], or transactions[] with hex per row). Do not paste only the unsigned prepared batch.'
    );
  };

  const handleSubmitPasted = async () => {
    if (!batchId) {
      toast.error('Prepare a batch first (missing batch id).');
      return;
    }
    setSubmitting(true);
    setSubmitResults(null);
    try {
      const signedTransactionHexes = parseSignedHexes(signedPaste);
      const expected = preparedBatch?.transactions?.length ?? 0;
      if (expected > 0 && signedTransactionHexes.length !== expected) {
        toast.error(
          `This batch has ${expected} prepared transaction(s), but the signed file has ${signedTransactionHexes.length}. For multiple transfers, use a results[] array with one signedTransactionHex per row (same order).`
        );
        return;
      }
      const { data } = await fuelInjectionApi.submit({ batchId, signedTransactionHexes });
      setSubmitResults(Array.isArray(data) ? data : []);
      toast.success('Broadcast completed.');
      await loadHistory();
    } catch (e) {
      if (e instanceof SyntaxError) {
        toast.error('Invalid JSON in signed payload.');
      } else {
        const msg = e.response?.data?.message || e.response?.data?.Message || e.message || 'Submit failed';
        toast.error(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const onSignedFile = (ev) => {
    const file = ev.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        setSignedPaste(String(reader.result || ''));
        toast.success('Loaded signed file. Review and broadcast when ready.');
      } catch {
        toast.error('Could not read file.');
      }
    };
    reader.readAsText(file);
    ev.target.value = '';
  };

  const txCount = preparedBatch?.transactions?.length ?? 0;

  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  return (
    <Page>
      <Title>
        <FiSend className="icon" />
        Fuel injection
      </Title>
      <Lead>
        Send native XCB from the admin wallet to authorized tokenizers (each row → that tokenizer&apos;s on-file wallet on
        Core Blockchain). One JSON batch for offline signing, then broadcast.
      </Lead>

      <FormPanel>
        <ManualSectionTitle>
          <FiZap style={{ color: '#4facfe' }} aria-hidden />
          Recipients
        </ManualSectionTitle>
        <Field>
          <span className="label">Admin wallet (from — pays native XCB)</span>
          <input
            id="admin-fuel-injection-signer"
            value={signerAddress}
            onChange={(e) => setSignerAddress(e.target.value)}
            placeholder="Core ICAN (44 hex) or 0x… — from admin profile wallet"
            autoComplete="off"
          />
          <span style={{ color: '#64748b', fontSize: '0.78rem' }}>
            Filled from your admin profile when available. Must match the offline signer key. Use the same format as on
            Core (ICAN or 0x).
          </span>
        </Field>
        {loadingList ? (
          <p style={{ color: '#94a3b8', margin: 0 }}>Loading tokenizers…</p>
        ) : (
          <>
            {rows.map((row) => (
              <RowGrid key={row.key}>
                <Field style={{ marginBottom: 0 }}>
                  <span className="label">Authorized tokenizer</span>
                  <select
                    value={row.authorizedTokenizerId}
                    onChange={(e) => updateRow(row.key, { authorizedTokenizerId: e.target.value })}
                  >
                    <option value="">Select tokenizer…</option>
                    {approvedTokenizers.map((t) => {
                      const id = t.id ?? t.Id;
                      return (
                        <option key={id} value={id}>
                          {tokenizerOptionLabel(t)}
                        </option>
                      );
                    })}
                  </select>
                </Field>
                <Field style={{ marginBottom: 0 }}>
                  <span className="label">Amount (XCB)</span>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={row.amountEth}
                    onChange={(e) => updateRow(row.key, { amountEth: e.target.value })}
                    placeholder="0.5"
                  />
                </Field>
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <IconBtn type="button" onClick={() => removeRow(row.key)} disabled={rows.length <= 1} title="Remove row">
                    <FiTrash2 />
                  </IconBtn>
                </div>
              </RowGrid>
            ))}
            <ActionRow style={{ marginBottom: 0 }}>
              <SecondaryButton type="button" onClick={addRow}>
                <FiPlus /> Add recipient
              </SecondaryButton>
              <SecondaryButton type="button" onClick={loadTokenizers} disabled={loadingList}>
                <FiRefreshCw /> Refresh list
              </SecondaryButton>
            </ActionRow>
          </>
        )}
      </FormPanel>

      <SectionDivider />

      <StatusPanel>
        <div className="title">Offline signing &amp; broadcast (Core Blockchain)</div>
        <FlowSteps>
          <li>
            <strong>Prepare unsigned batch</strong> — The API builds one JSON file with multiple native transfer transactions
            (sequential nonces). Private keys never leave your offline signer.
          </li>
          <li>
            <strong>Sign</strong> — Copy or download the batch JSON, then sign every <code>transactions[]</code> entry in order
            with the Core offline signer (same admin wallet as <code>from</code>).
          </li>
          <li>
            <strong>Upload or paste signed data</strong> — Load the signer output (batch JSON with <code>results[]</code> and
            each <code>signedTransactionHex</code>) or paste it below. Then <strong>Broadcast signed batch</strong> so the
            backend submits <code>xcb_sendRawTransaction</code> for each transaction in order.
          </li>
        </FlowSteps>
        <div className="meta">
          {txCount > 0 ? (
            <>
              <span>Batch id: {batchId ? String(batchId) : '—'}</span>
              <span>Transfers: {txCount}</span>
              <span>From: {signerAddress ? shortenAddr(signerAddress) : '—'}</span>
            </>
          ) : (
            <span>Set recipients above, then prepare the unsigned batch.</span>
          )}
        </div>
      </StatusPanel>

      {preparedBatch && (
        <>
          <PayloadBox>
            <div className="title">Unsigned native XCB transfer batch (JSON)</div>
            <div className="hint">Sign offline only — do not sign in the browser.</div>
            <PayloadActions>
              <MiniButton type="button" onClick={handleCopyBatchJson}>
                <FiCopy aria-hidden />
                Copy JSON
              </MiniButton>
              <MiniButton type="button" onClick={handleDownloadBatchJson}>
                <FiDownload aria-hidden />
                Download JSON
              </MiniButton>
            </PayloadActions>
            <textarea readOnly value={preparedBatchJson} spellCheck={false} />
          </PayloadBox>

          <PayloadBox>
            <div className="title">Signer output shape</div>
            <div className="hint">
              For <strong>several</strong> recipients, use <code>results[]</code> (one <code>signedTransactionHex</code> per
              prepared transaction, same order). For a <strong>single</strong> transfer, many signers output one JSON object
              with <code>signedTransactionHex</code> at the top level — that is accepted too. A JSON array of raw hex
              strings is also supported.
            </div>
            <PayloadActions>
              <MiniButton
                type="button"
                onClick={async () => {
                  await copyTextToClipboard(BATCH_SIGNER_TEMPLATE);
                  toast.success('Copied template');
                }}
              >
                <FiCopy aria-hidden />
                Copy template
              </MiniButton>
            </PayloadActions>
            <textarea readOnly value={BATCH_SIGNER_TEMPLATE} spellCheck={false} />
          </PayloadBox>

          <PayloadBox>
            <div className="title">Signed batch — upload or paste</div>
            <div className="hint">
              Upload or paste signer JSON: <code>results[]</code> batch, a single-object file with{' '}
              <code>signedTransactionHex</code>, or an array of hex strings.
            </div>
            <PayloadActions>
              <FilePickLabel>
                <FiUpload aria-hidden />
                Upload signed file
                <input type="file" accept=".json,.txt,text/json,application/json" onChange={onSignedFile} />
              </FilePickLabel>
            </PayloadActions>
            <textarea
              value={signedPaste}
              onChange={(e) => setSignedPaste(e.target.value)}
              placeholder="Paste signed JSON (results[], single signedTransactionHex object, or [&quot;0x…&quot;])…"
              spellCheck={false}
            />
          </PayloadBox>
        </>
      )}

      <ActionRow>
        <SecondaryButton
          type="button"
          onClick={resetOfflineFlow}
          disabled={preparing || submitting || (!preparedBatch && !signedPaste.trim() && !submitResults)}
        >
          <FiRefreshCw aria-hidden />
          Clear prepared / signed
        </SecondaryButton>
        <PrimaryButton
          type="button"
          onClick={handlePrepare}
          disabled={preparing || loadingList || Boolean(preparedBatch)}
        >
          <FiCpu aria-hidden />
          {preparing ? 'Preparing…' : 'Prepare unsigned batch'}
        </PrimaryButton>
        <PrimaryButton
          type="button"
          onClick={handleSubmitPasted}
          disabled={submitting || !preparedBatch || !batchId || !signedPaste.trim()}
        >
          <FiCpu aria-hidden />
          {submitting ? 'Broadcasting…' : 'Broadcast signed batch'}
        </PrimaryButton>
      </ActionRow>

      {submitResults && submitResults.length > 0 && (
        <PayloadBox>
          <div className="title">Broadcast results</div>
          <div className="hint">One row per submitted transaction. Open the explorer to verify on-chain.</div>
          <ResultsTableWrap>
            <ResultsTable>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Transaction</th>
                  <th>Status</th>
                  <th style={{ whiteSpace: 'nowrap' }}>Explorer</th>
                </tr>
              </thead>
              <tbody>
                {submitResults.map((r) => {
                  const idx = r.index ?? r.Index;
                  const hash = r.transactionHash ?? r.TransactionHash;
                  const st = r.receiptStatus ?? r.ReceiptStatus;
                  const rr = r.revertReason ?? r.RevertReason;
                  let statusLabel = 'Pending / unknown';
                  if (isSuccessfulReceiptStatus(st)) statusLabel = 'Confirmed';
                  else if (isFailedReceiptStatus(st)) statusLabel = 'Failed';
                  const url =
                    explorerConfigured && hash && String(hash).trim().startsWith('0x')
                      ? transactionExplorerUrl(String(hash).trim())
                      : '';
                  return (
                    <tr key={idx}>
                      <td>{idx}</td>
                      <td className="mono">
                        {hash}
                        {rr ? ` — ${rr}` : ''}
                      </td>
                      <td>{statusLabel}</td>
                      <td>
                        <MiniButton
                          type="button"
                          aria-label={`View transaction ${idx} in block explorer`}
                          disabled={!url}
                          title={
                            url
                              ? 'Open transaction in block explorer'
                              : explorerConfigured
                                ? 'No transaction hash'
                                : 'Block explorer URL not loaded from API'
                          }
                          onClick={() => {
                            if (url) window.open(url, '_blank', 'noopener,noreferrer');
                          }}
                        >
                          <FiExternalLink aria-hidden />
                        </MiniButton>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </ResultsTable>
          </ResultsTableWrap>
        </PayloadBox>
      )}

      <SectionDivider />

      <PayloadBox>
        <div className="title">Fuel injection history</div>
        <div className="hint">
          Recent batches (prepare + on-chain results after broadcast). Same data is stored server-side for audit.
        </div>
        <PayloadActions>
          <SecondaryButton type="button" onClick={loadHistory} disabled={historyLoading}>
            <FiRefreshCw aria-hidden />
            {historyLoading ? 'Loading…' : 'Refresh history'}
          </SecondaryButton>
        </PayloadActions>
        <ResultsTableWrap>
          <ResultsTable>
            <thead>
              <tr>
                <th>Created</th>
                <th>Status</th>
                <th>#</th>
                <th>From</th>
                <th>Submitted</th>
                <th>Recipients</th>
                <th>On-chain</th>
              </tr>
            </thead>
            <tbody>
              {history.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ color: '#94a3b8' }}>
                    {historyLoading ? 'Loading…' : 'No batches yet.'}
                  </td>
                </tr>
              ) : (
                history.map((row) => {
                  const id = row.id ?? row.Id;
                  const created = row.createdAtUtc ?? row.CreatedAtUtc;
                  const submittedAt = row.submittedAtUtc ?? row.SubmittedAtUtc;
                  const st = row.status ?? row.Status;
                  const count = row.transferCount ?? row.TransferCount ?? 0;
                  const from = row.signerAddress ?? row.SignerAddress ?? '';
                  const recipients = row.recipients ?? row.Recipients ?? [];
                  const chain = row.blockchainResults ?? row.BlockchainResults ?? null;
                  const recSummary = recipients.length
                    ? recipients
                        .map(
                          (x) =>
                            `${x.organizationName ?? x.OrganizationName ?? '?'}: ${x.amountEth ?? x.AmountEth ?? ''} XCB`
                        )
                        .join('; ')
                    : '—';
                  return (
                    <tr key={id}>
                      <td className="mono" style={{ whiteSpace: 'nowrap' }}>
                        {created ? new Date(created).toLocaleString() : '—'}
                      </td>
                      <td>{fuelInjectionStatusLabel(st)}</td>
                      <td>{count}</td>
                      <td className="mono">{from ? shortenAddr(from) : '—'}</td>
                      <td className="mono" style={{ whiteSpace: 'nowrap' }}>
                        {submittedAt ? new Date(submittedAt).toLocaleString() : '—'}
                      </td>
                      <td style={{ maxWidth: '220px', fontSize: '0.78rem', color: '#cbd5e1' }}>{recSummary}</td>
                      <td>
                        {Array.isArray(chain) && chain.length > 0 ? (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem' }}>
                            {chain.map((tx) => {
                              const idx = tx.index ?? tx.Index;
                              const hash = tx.transactionHash ?? tx.TransactionHash;
                              const url =
                                explorerConfigured && hash && String(hash).trim().startsWith('0x')
                                  ? transactionExplorerUrl(String(hash).trim())
                                  : '';
                              return (
                                <MiniButton
                                  key={idx}
                                  type="button"
                                  aria-label={`Open transaction ${idx} in explorer`}
                                  disabled={!url}
                                  title={url ? `Tx ${idx}` : 'No explorer / hash'}
                                  onClick={() => {
                                    if (url) window.open(url, '_blank', 'noopener,noreferrer');
                                  }}
                                >
                                  <FiExternalLink aria-hidden />
                                </MiniButton>
                              );
                            })}
                          </div>
                        ) : (
                          <span style={{ color: '#64748b' }}>—</span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </ResultsTable>
        </ResultsTableWrap>
      </PayloadBox>
    </Page>
  );
}

export default FuelInjection;
